<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';

const emit = defineEmits(['send-location']);

const { t } = useI18n();

const dialogRef = ref(null);
const isResolvingLocation = ref(false);
const form = ref({
  name: '',
  address: '',
  mapUrl: '',
  latitude: '',
  longitude: '',
});

const locationPreview = computed(() => {
  if (form.value.mapUrl?.trim() && parseMapPoint(form.value.mapUrl)) {
    return form.value.mapUrl.trim();
  }

  if (!form.value.latitude || !form.value.longitude) {
    return '';
  }

  return `https://maps.google.com/?q=${form.value.latitude},${form.value.longitude}`;
});

const resetForm = () => {
  form.value = {
    name: '',
    address: '',
    mapUrl: '',
    latitude: '',
    longitude: '',
  };
};

const COORDINATE_PATTERN = /(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/;

const parseMapPoint = rawValue => {
  const value = rawValue?.trim();
  if (!value) return null;

  try {
    const url = new URL(value);
    const candidates = [
      url.searchParams.get('q'),
      url.searchParams.get('query'),
      url.searchParams.get('ll'),
      decodeURIComponent(url.pathname || ''),
    ].filter(Boolean);

    for (const candidate of candidates) {
      const match = candidate.match(COORDINATE_PATTERN);
      if (match) {
        return {
          latitude: Number(match[1]),
          longitude: Number(match[2]),
        };
      }
    }
  } catch {
    const match = value.match(COORDINATE_PATTERN);
    if (match) {
      return {
        latitude: Number(match[1]),
        longitude: Number(match[2]),
      };
    }
  }

  return null;
};

const applyMapUrl = () => {
  if (!form.value.mapUrl?.trim()) return true;

  const parsedPoint = parseMapPoint(form.value.mapUrl);
  if (!parsedPoint) {
    if (form.value.latitude && form.value.longitude) {
      return true;
    }

    useAlert(t('CONVERSATION.REPLYBOX.LOCATION_INVALID_MAP_LINK'));
    return false;
  }

  form.value.latitude = parsedPoint.latitude.toFixed(6);
  form.value.longitude = parsedPoint.longitude.toFixed(6);
  return true;
};

const open = async () => {
  dialogRef.value?.open();
  await nextTick();
};

const close = () => {
  dialogRef.value?.close();
};

const handleDialogClose = () => {
  isResolvingLocation.value = false;
  resetForm();
};

const useCurrentLocation = () => {
  if (!navigator.geolocation) {
    useAlert(t('CONVERSATION.REPLYBOX.LOCATION_UNAVAILABLE'));
    return;
  }

  isResolvingLocation.value = true;
  navigator.geolocation.getCurrentPosition(
    position => {
      form.value.latitude = position.coords.latitude.toFixed(6);
      form.value.longitude = position.coords.longitude.toFixed(6);
      isResolvingLocation.value = false;
    },
    () => {
      isResolvingLocation.value = false;
      useAlert(t('CONVERSATION.REPLYBOX.LOCATION_PERMISSION_ERROR'));
    },
    {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    }
  );
};

const submit = () => {
  if (!applyMapUrl()) return;

  const latitude = Number(form.value.latitude);
  const longitude = Number(form.value.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    useAlert(t('CONVERSATION.REPLYBOX.LOCATION_INVALID_COORDINATES'));
    return;
  }

  const name = form.value.name.trim();
  const address = form.value.address.trim();
  const fallbackTitle =
    [name || null, address || null].filter(Boolean).join(', ') ||
    `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

  emit('send-location', {
    name,
    address,
    latitude,
    longitude,
    fallback_title: fallbackTitle,
    external_url: locationPreview.value,
  });

  resetForm();
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('CONVERSATION.REPLYBOX.LOCATION_TITLE')"
    :description="t('CONVERSATION.REPLYBOX.LOCATION_DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="handleDialogClose"
  >
    <div class="grid gap-4">
      <div class="grid gap-2 sm:grid-cols-2">
        <label class="location-field sm:col-span-2">
          <span class="location-field__label">
            {{ t('CONVERSATION.REPLYBOX.LOCATION_MAP_LINK') }}
          </span>
          <input
            v-model="form.mapUrl"
            class="location-field__input"
            type="url"
            :placeholder="
              t('CONVERSATION.REPLYBOX.LOCATION_MAP_LINK_PLACEHOLDER')
            "
            @blur="applyMapUrl"
          />
          <span class="location-field__hint">
            {{ t('CONVERSATION.REPLYBOX.LOCATION_MAP_LINK_HINT') }}
          </span>
        </label>
        <label class="location-field">
          <span class="location-field__label">
            {{ t('CONVERSATION.REPLYBOX.LOCATION_NAME') }}
          </span>
          <input v-model="form.name" class="location-field__input" type="text" />
        </label>
        <label class="location-field">
          <span class="location-field__label">
            {{ t('CONVERSATION.REPLYBOX.LOCATION_ADDRESS') }}
          </span>
          <input
            v-model="form.address"
            class="location-field__input"
            type="text"
          />
        </label>
      </div>
      <div class="grid gap-2 sm:grid-cols-2">
        <label class="location-field">
          <span class="location-field__label">
            {{ t('CONVERSATION.REPLYBOX.LOCATION_LATITUDE') }}
          </span>
          <input
            v-model="form.latitude"
            class="location-field__input"
            type="number"
            step="any"
          />
        </label>
        <label class="location-field">
          <span class="location-field__label">
            {{ t('CONVERSATION.REPLYBOX.LOCATION_LONGITUDE') }}
          </span>
          <input
            v-model="form.longitude"
            class="location-field__input"
            type="number"
            step="any"
          />
        </label>
      </div>
      <div class="flex justify-start">
        <Button
          type="button"
          variant="faded"
          color="slate"
          icon="i-ph-map-pin-line"
          :label="t('CONVERSATION.REPLYBOX.LOCATION_USE_CURRENT')"
          :is-loading="isResolvingLocation"
          @click="useCurrentLocation"
        />
      </div>
      <a
        v-if="locationPreview"
        :href="locationPreview"
        class="location-preview-link"
        rel="noreferrer noopener nofollow"
        target="_blank"
      >
        {{ t('COMPONENTS.LOCATION_BUBBLE.SEE_ON_MAP') }}
      </a>
    </div>
    <template #footer>
      <div class="flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          class="w-full"
          @click="close"
        />
        <Button
          type="button"
          color="blue"
          :label="t('CONVERSATION.REPLYBOX.LOCATION_SEND')"
          class="w-full"
          :disabled="isResolvingLocation"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped lang="scss">
.location-field {
  @apply flex flex-col gap-1.5;
}

.location-field__label {
  @apply text-xs font-medium text-n-slate-11;
}

.location-field__input {
  @apply h-10 rounded-lg border border-n-strong bg-n-background px-3 text-sm text-n-slate-12 outline-none transition-colors;

  &:focus {
    @apply border-n-brand;
  }
}

.location-preview-link {
  @apply inline-flex text-sm text-n-brand hover:underline;
}

.location-field__hint {
  @apply text-xs text-n-slate-10;
}
</style>
